import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiBaseUrls, ApiControllers } from '../constants/constants';
import { PaginatedListModel } from '../models/service/paginated-list.model';
import { PaginationResponseHeader } from '../models/service/pagination-response-header.model';
import { PropertiesFilter } from '../filters/properties.filter';
import { CreatePropertyRequest, UpdatePropertyRequest, PropertyModel } from '../models/data/property.model';
import { ApiErrorInfoModel } from '../models/service/error-info.model';
import { ServiceResultModel } from '../models/service/service-result.model';
import { APISanitizerUtilities } from '../utilities/api-sanitizer.utilities';
import { MemberPropertyModel } from '../models/data/member-property.model';
import { AuditLog } from '../models/service/audit-log.model';
import { PropertyAuditLogModel } from '../models/data/property-audit-log.model';
import { AuditLogMapper } from '../utilities/audit-log-mapper.utilities';
import { ServiceUtilities } from '../utilities/service.utilities';
import { PropertiesMessagingConstants } from '../constants/messaging.constants';
import { BulkActionRequest } from '../models/service/bulk-action-request.model';
import { BulkActionResponse } from '../models/service/bulk-action-response.model';
import { PropertyBulkActions } from '../enums/bulk-actions.enums';

@Injectable({
  providedIn: 'root'
})
export class PropertiesService {
  private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.PROPERTIES()}`;

  constructor(private httpClient: HttpClient) { }

  public async searchProperties(propertiesFilter: PropertiesFilter, orderBy?: string, skip?: number, take?: number, includePropertyMembers?: boolean): Promise<ServiceResultModel<PaginatedListModel<PropertyModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      const requestUrl = this.apiPath + `/filter?orderBy=${orderBy}&skip=${skip}&take=${take}&includePropertyMembers=${includePropertyMembers ?? false}`;

      const response = await firstValueFrom(
        this.httpClient.post<PropertyModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<PropertiesFilter>(propertiesFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<PropertyModel> = {
        data: <PropertyModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createProperty(property: CreatePropertyRequest): Promise<ServiceResultModel<PropertyModel | undefined>> {
    try {
      const requestUrl = this.apiPath;

      const response = await firstValueFrom(
        this.httpClient.post<PropertyModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<CreatePropertyRequest>(property)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const organization = <PropertyModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: organization, success: true, message: PropertiesMessagingConstants.PROPERTIES_ADD_PROPERTY_SUCCESS(), statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateProperty(propertyId: number, request: UpdatePropertyRequest): Promise<ServiceResultModel<PropertyModel | undefined>> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}`;

      const response = await firstValueFrom(
        this.httpClient.patch<PropertyModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdatePropertyRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const property = <PropertyModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: property, success: true, message: PropertiesMessagingConstants.PROPERTIES_EDIT_PROPERTY_SUCCESS(), statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getPropertyById(propertyId: number, includePropertyMembers: boolean = false): Promise<ServiceResultModel<PropertyModel>> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}`;
      const params: any = {};
      if (includePropertyMembers) params.includePropertyMembers = true;
      const response = await firstValueFrom(this.httpClient.get<PropertyModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }, params }));

      const property = <PropertyModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: property, success: true, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async addMemberToProperty(propertyId: number, memberProperty: MemberPropertyModel): Promise<ServiceResultModel> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}/members`;

      const response = await firstValueFrom(
        this.httpClient.post(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<MemberPropertyModel>(memberProperty)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel>{ success: true, message: PropertiesMessagingConstants.PROPERTIES_ADD_MEMBER_TO_PROPERTY_SUCCESS(), statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async addMembersToProperty(propertyId: number, memberProperty: MemberPropertyModel): Promise<ServiceResultModel> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}/members`;

      const response = await firstValueFrom(
        this.httpClient.post<undefined | ApiErrorInfoModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<MemberPropertyModel>(memberProperty)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel>{ success: true, message: PropertiesMessagingConstants.PROPERTIES_ADD_MEMBERS_TO_PROPERTY_SUCCESS(), statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async removeMemberFromProperty(propertyId: number, memberProperty: MemberPropertyModel): Promise<ServiceResultModel> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}/members`;

      const response = await firstValueFrom(
        this.httpClient.patch<undefined | ApiErrorInfoModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<MemberPropertyModel>(memberProperty)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel>{ success: true, message: PropertiesMessagingConstants.PROPERTIES_REMOVE_MEMBER_FROM_PROPERTY_SUCCESS(), statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async removeMembersFromProperty(propertyId: number, memberProperty: MemberPropertyModel): Promise<ServiceResultModel> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}/members`;

      const response = await firstValueFrom(
        this.httpClient.patch<undefined | ApiErrorInfoModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<MemberPropertyModel>(memberProperty)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel>{ success: true, message: PropertiesMessagingConstants.PROPERTIES_REMOVE_MEMBERS_FROM_PROPERTY_SUCCESS(), statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async deleteProperty(propertyId: number, note?: string): Promise<ServiceResultModel<PropertyModel>> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}`;
      const response = await firstValueFrom(
        this.httpClient.delete<PropertyModel>(
          requestUrl,
          {
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
            observe: "response",
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          }
        ));
      const property = <PropertyModel>response.body?.valueOf();

      return <ServiceResultModel>{ data: property, success: true, message: PropertiesMessagingConstants.PROPERTIES_DELETE_SUCCESS(), statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updatePropertyStatus(propertyId: number, active: boolean, note?: string): Promise<ServiceResultModel<PropertyModel>> {
    try {
      const requestUrl = this.apiPath + `/${propertyId}/status?active=${active}`;

      const response = await firstValueFrom(
        this.httpClient.patch<PropertyModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const updatedProperty = <PropertyModel>response.body?.valueOf();

      const messageData = {actionText: active ? "activated" : "deactivated"};
      const message = PropertiesMessagingConstants.PROPERTIES_UPDATE_STATUS_SUCCESS().replaceValuesWithObject(messageData);

      return <ServiceResultModel>{ data: updatedProperty, success: true, message: message, statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkUpdatePropertyStatus(bulkAction: PropertyBulkActions, bulkActionRequest: BulkActionRequest<PropertiesFilter | null>): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/bulk/status/${bulkAction}`;

      const response = await firstValueFrom(
        this.httpClient.patch<BulkActionResponse>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<PropertiesFilter | null>>(bulkActionRequest)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const bulkActionResponse = <BulkActionResponse>response.body?.valueOf();

      const successMessageData = {
        actionText: (bulkAction === PropertyBulkActions.Activate) ? "activated" : "deactivated",
        totalAffected: bulkActionResponse.totalAffected
      };
      const successMessage = PropertiesMessagingConstants.PROPERTIES_BULK_STATUS_SUCCESS().replaceValuesWithObject(successMessageData);

      return <ServiceResultModel>{ data: bulkActionResponse, success: true, message: successMessage, statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkDeleteProperties(bulkActionRequest: BulkActionRequest<PropertiesFilter | null>): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/bulk`;

      const response = await firstValueFrom(
        this.httpClient.delete<BulkActionResponse>(
          requestUrl,
          {
            observe: "response",
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<PropertiesFilter | null>>(bulkActionRequest)),
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          }
        ));

      const bulkActionResponse = <BulkActionResponse>response.body?.valueOf();

      const successMessageData = {totalAffected: bulkActionResponse.totalAffected};
      const successMessage = PropertiesMessagingConstants.PROPERTIES_BULK_DELETE_SUCCESS().replaceValuesWithObject(successMessageData);

      return <ServiceResultModel>{ data: bulkActionResponse, success: true, message: successMessage, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getAuditLogsByPropertyId(propertyId: number, skip: number, take: number, includeDetails: boolean = false): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      let requestUrl = this.apiPath + `/${propertyId}/logs?skip=${skip}&take=${take}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(this.httpClient.get<PropertyAuditLogModel[]>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const mappedData = AuditLogMapper.mapPropertyAuditLogs(<PropertyAuditLogModel[]>response.body?.valueOf());

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, message: "Audit logs loaded successfully." };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}